/* eslint-disable */
import React, { useState, useEffect, useContext } from 'react';
import './styles.css';
import 'hammerjs';
import {
  Chart,
  ChartSeries,
  ChartSeriesItem,
  ChartCategoryAxis,
  ChartCategoryAxisItem,
  ChartAxisDefaults,
  ChartTooltip,
  ChartLegend
} from '@progress/kendo-react-charts';
import { Card, CardTitle, CardBody, CardActions, CardImage } from '@progress/kendo-react-layout';
import { IntlProvider, load, LocalizationProvider, loadMessages } from '@progress/kendo-react-intl';
import 'react-datepicker/dist/react-datepicker.css';
import likelySubtags from 'cldr-core/supplemental/likelySubtags.json';
import weekData from 'cldr-core/supplemental/weekData.json';
import caGregorian from 'cldr-dates-full/main/pt/ca-gregorian.json';
import dateFields from 'cldr-dates-full/main/pt/dateFields.json';
import numbers from 'cldr-numbers-full/main/pt/numbers.json';
import ptMessages from '../../utils/pt.json';
import { toMoney } from '../../utils/formatter';
import { Calendar } from '../../components/dateComponents';
import { AuthContext } from '../../auth/AuthContext';
import { errorNotification } from '../../components/notification';
import SaleDay from '../../assets/sale-day.svg';
import SaleWeek from '../../assets/sale-week.svg';
import SaleMonth from '../../assets/sale-month.svg';
import SaleYear from '../../assets/sale-year.svg';
import * as saleService from '../../services/sale';
import * as expenseService from '../../services/expense';
import LoadScreen from '../load';
import { DateNow } from '../../utils/date';

load(likelySubtags, weekData, caGregorian, dateFields, numbers);
loadMessages(ptMessages, 'pt');

const MONTHS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

const buildMonthArray = (values) => {
  const arr = new Array(12).fill(0);
  values?.forEach((e) => {
    arr[new Date(e.Data).getMonth()] = e.Valor;
  });
  return arr;
};

const initialDashboard = {
  VendaDiaria: 0,
  VendaSemanal: 0,
  VendaMensal: 0,
  VendaAnual: 0,
  VendasMensais: []
};

const Home = () => {
  const { walletSelected } = useContext(AuthContext);
  const [date, setDate] = useState(DateNow());
  const [chartData, setChartData] = useState([0]);
  const [chartDataDespesasOperacionais, setChartDataDespesasOperacionais] = useState([0]);
  const [chartDataRH, setChartDataRH] = useState([0]);
  const [chartMonths, setChartMonths] = useState([]);

  const temPermissaoCompras =
    walletSelected?.ListaPermissoes?.includes('1') ||
    walletSelected?.ListaPermissoes?.includes('31');
  const temPermissaoRH =
    walletSelected?.ListaPermissoes?.includes('1') ||
    walletSelected?.ListaPermissoes?.includes('32');
  const [dashBoard, setDashboard] = useState(initialDashboard);
  const [loading, setLoading] = useState(true);

  const loadDashboard = async () => {
    const dateInterval = { DataInicio: date, DataFim: date };
    const [saleResp, expenseResp] = await Promise.all([
      saleService.saleDashboard(dateInterval),
      expenseService.expenseDashboard(dateInterval)
    ]);
    setDashboard(saleResp.data);
    setChartData(buildMonthArray(saleResp.data.VendasMensais));
    setChartDataDespesasOperacionais(buildMonthArray(expenseResp.data.DespesasOperacionaisMensais));
    setChartDataRH(buildMonthArray(expenseResp.data.RecursosMensais));
    setChartMonths(MONTHS);
  };

  useEffect(async () => {
    setLoading(true);
    if (walletSelected.CarteiraBusiness == null) {
      errorNotification(
        'Não é possível a utilização do sistema pois não existe um carteira Next Business associada a carteira do Next Finance.'
      );
    } else {
      await loadDashboard();
    }
    setLoading(false);
  }, [date]);

  const SharedTooltip = ({ points }) => {
    if (!points || points.length === 0) return null;
    const idx = points[0].categoryIndex;
    const receita = chartData[idx] ?? 0;
    const despesasOperacionais = chartDataDespesasOperacionais[idx] ?? 0;
    const fmt = (v) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    const temAmbas = temPermissaoCompras && temPermissaoRH;
    return (
      <div style={{ padding: '8px 12px', minWidth: 220 }}>
        <strong>{MONTHS[idx]}</strong>
        <div style={{ marginTop: 6 }}>
          <span style={{ color: '#1FA363' }}>■</span> Receitas: {fmt(receita)}
          <br />
          {temPermissaoRH && (
            <>
              <span style={{ color: '#C0504D' }}>■</span> Recursos Humanos:{' '}
              {fmt(chartDataRH[idx] ?? 0)}
              <br />
            </>
          )}
          {temPermissaoCompras && (
            <>
              <span style={{ color: '#ED9C28' }}>■</span> Compras:{' '}
              {fmt(despesasOperacionais)}
              <br />
            </>
          )}
        </div>
        {temAmbas && (
          <>
            <hr style={{ margin: '6px 0' }} />
            <strong>Despesa total: {fmt((chartDataRH[idx] ?? 0) + despesasOperacionais)}</strong>
            <br />
            <strong>
              Resultado: {fmt(receita - ((chartDataRH[idx] ?? 0) + despesasOperacionais))}
            </strong>
          </>
        )}
      </div>
    );
  };

  const ChartContainer = () => (
    <LocalizationProvider language="pt">
      <IntlProvider locale="pt">
        <Chart>
          <ChartLegend position="top" orientation="horizontal" />
          <ChartAxisDefaults labels={{ format: 'n2' }} />
          <ChartTooltip shared render={SharedTooltip} />
          <ChartCategoryAxis>
            <ChartCategoryAxisItem categories={chartMonths} />
          </ChartCategoryAxis>
          <ChartSeries>
            <ChartSeriesItem
              name="Receitas"
              color="#1FA363"
              type="column"
              data={chartData}
              stack={{ group: 'receitas' }}
              markers={{ visible: false }}
            />
            {temPermissaoRH && (
              <ChartSeriesItem
                name="Recursos Humanos"
                color="#C0504D"
                type="column"
                data={chartDataRH}
                stack={{ group: 'despesas' }}
                markers={{ visible: false }}
              />
            )}
            {temPermissaoCompras && (
              <ChartSeriesItem
                name="Despesas Operacionais"
                color="#ED9C28"
                type="column"
                data={chartDataDespesasOperacionais}
                stack={{ group: 'despesas' }}
                markers={{ visible: false }}
              />
            )}
          </ChartSeries>
        </Chart>
      </IntlProvider>
    </LocalizationProvider>
  );

  return loading ? (
    <LoadScreen />
  ) : (
    <>
      <h1>Início</h1>
      <div className="buttons-home">
        <Calendar date={date} setDate={setDate} />
      </div>
      <div>
        <div className="k-card-deck">
          <Card orientation="horizontal" layout="end">
            <CardImage src={SaleDay} />
            <div className="k-vbox">
              <CardBody>
                <CardTitle style={{ color: '#5F6CAA' }}>Receitas Diárias</CardTitle>
              </CardBody>
              <CardActions layout="end">
                <CardTitle>{toMoney(dashBoard.VendaDiaria, true)}</CardTitle>
              </CardActions>
            </div>
          </Card>
          <Card orientation="horizontal" layout="end">
            <CardImage src={SaleWeek} />
            <div className="k-vbox">
              <CardBody>
                <CardTitle style={{ color: '#5DA57D' }}>Receitas Semanais</CardTitle>
              </CardBody>
              <CardActions layout="end">
                <CardTitle>{toMoney(dashBoard.VendaSemanal, true)}</CardTitle>
              </CardActions>
            </div>
          </Card>
          <Card orientation="horizontal" layout="end">
            <CardImage src={SaleMonth} />
            <div className="k-vbox">
              <CardBody>
                <CardTitle style={{ color: '#FF8213' }}>Receitas Mensais</CardTitle>
              </CardBody>
              <CardActions layout="end">
                <CardTitle>{toMoney(dashBoard.VendaMensal, true)}</CardTitle>
              </CardActions>
            </div>
          </Card>
          <Card orientation="horizontal" layout="end">
            <CardImage src={SaleYear} />
            <div className="k-vbox">
              <CardBody>
                <CardTitle style={{ color: '#00A6D2' }}>Receitas Anuais</CardTitle>
              </CardBody>
              <CardActions layout="end">
                <CardTitle>{toMoney(dashBoard.VendaAnual, true)}</CardTitle>
              </CardActions>
            </div>
          </Card>
        </div>
      </div>
      <div className="container-card">
        <h2 className="card-header" style={{ color: '#FF8213', borderBottomColor: '#FF8213' }}>
          Receitas e Despesas Anuais
        </h2>
        <div className="card-content">
          <span className="valueInReal">Valores em [R$]</span>
          <ChartContainer />
        </div>
      </div>
    </>
  );
};

export default Home;
